import {Alert, Box, Button, Card, CardContent, CircularProgress, FormControl, Grid, IconButton, InputAdornment, Snackbar, TextField, Tooltip, Typography} from '@mui/material';
import React, {useEffect, useMemo, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import axios from 'axios';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import InstagramIcon from '@mui/icons-material/Instagram';

import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import ChevronRight from '@mui/icons-material/ChevronRight';
import TrendingUp from '@mui/icons-material/TrendingUp';
import useMediaQuery from '@mui/material/useMediaQuery';
import {LanguageSelector} from './LanguageSelector';
import {getApiBaseUrl, getAppUrl, getStartUrl, isCustomerControlledDomain, showSocialIcons} from './UrlUtil';

const axiosClient = axios.create({
	baseURL: getApiBaseUrl(),
	withCredentials: true,
	headers: {'Content-Type': 'application/json'},
});

// Only shown on Pifa's own brand domains (pifa.se, engros.ai, partihandel.se)
// — these are Pifa/partihandel's own social accounts and have no business
// appearing on any tenant's branded login page. See showSocialIcons in
// UrlUtil.js for the exact domain allowlist.
const SocialIcons = ({size = '2.5rem'}) => {
	if (!showSocialIcons) return null;

	return (
		<>
			<a href="https://instagram.com/partihandel.se" style={{color: 'white'}}>
				<InstagramIcon sx={{fontSize: size}} />
			</a>
			<a href="https://linkedin.com/company/partihanteraren" style={{color: 'white'}}>
				<LinkedInIcon sx={{fontSize: size}} />
			</a>
			<a href="https://wa.me/+46737133797?text='_'" style={{color: 'white'}}>
				<WhatsAppIcon sx={{fontSize: size}} />
			</a>
			<a href="https://tiktok.com/@partihandel">
				<Tooltip title="tiktok.com/@partihandel">
					<svg fill="#ffffff" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 50 50" width={size} height={size}>
						<path d="M41,4H9C6.243,4,4,6.243,4,9v32c0,2.757,2.243,5,5,5h32c2.757,0,5-2.243,5-5V9C46,6.243,43.757,4,41,4z M37.006,22.323 c-0.227,0.021-0.457,0.035-0.69,0.035c-2.623,0-4.928-1.349-6.269-3.388c0,5.349,0,11.435,0,11.537c0,4.709-3.818,8.527-8.527,8.527 s-8.527-3.818-8.527-8.527s3.818-8.527,8.527-8.527c0.178,0,0.352,0.016,0.527,0.027v4.202c-0.175-0.021-0.347-0.053-0.527-0.053 c-2.404,0-4.352,1.948-4.352,4.352s1.948,4.352,4.352,4.352s4.527-1.894,4.527-4.298c0-0.095,0.042-19.594,0.042-19.594h4.016 c0.378,3.591,3.277,6.425,6.901,6.685V22.323z" />
					</svg>
				</Tooltip>
			</a>
		</>
	);
};

// Pre-fetch fallback. Used only for the brief moment before /api/branding
// resolves. Kept intentionally bland (default partihandel grey) so users on
// a tenant subdomain don't see another tenant's colors flash. The real
// per-tenant config lives in the database, fetched via the loadbalancer.
// On a customer-controlled domain (e.g. id.lotusfoodservice.se) the
// partihandel.se logo must never flash before that per-tenant logo loads.
const FALLBACK_BRANDING = {
	logoUrl: isCustomerControlledDomain ? null : 'https://storage.googleapis.com/pifa.se/partihandel.se_row.png',
	logoStyle: {},
	primaryColor: '#365F62',
	primaryColorHover: '#2a4b50',
	buttonTextColor: '#ffffff',
	// White/near-black text: the form fields below (TextField, labels, icons)
	// are unstyled MUI defaults designed for a light card — they were never
	// made dark-card-aware. A dark default here would make them illegible.
	cardBackground: '#ffffff',
	cardTextColor: '#1a1a1a',
	rightPanelGradient: 'linear-gradient(135deg, #112427, #365f62)',
	leftBackgroundImage: null,
	fullScreenBackgroundImage: false,
	title: 'partihandel.se • Logga in',
};

const TENANT_GROUP_KEYS = ['active', 'trading', 'inactive'];
const TENANT_GROUP_META = {
	active: {label: 'Aktiva företag', color: '#2e7d32'},
	trading: {label: 'Trading only', color: '#616161'},
	inactive: {label: 'Inaktiva företag', color: '#c62828'},
};
const getTenantGroupKey = c => (c.tradingOnly ? 'trading' : c.isActive === false ? 'inactive' : 'active');

// Leading row indicator: green dot / red dot / trending icon, matching
// the Settings → User company picker.
const TenantRowIndicator = ({groupKey}) => {
	if (groupKey === 'trading') return <TrendingUp fontSize="small" sx={{color: '#616161'}} />;
	const color = groupKey === 'inactive' ? '#c62828' : '#2e7d32';
	return <Box sx={{width: 10, height: 10, borderRadius: '50%', bgcolor: color, flexShrink: 0}} />;
};

function LoginApp() {
	const [loading, setLoading] = useState(false);
	const {t} = useTranslation();
	const [updateMessage, setUpdateMessage] = useState('');
	const [updateType, setUpdateType] = useState('info');
	const [snackbarOpen, setSnackbarOpen] = useState(false);
	const isMobileScreen = useMediaQuery('(max-width:700px)');

	const [showPassword, setShowPassword] = useState(false);
	const handleClickShowPassword = () => setShowPassword(!showPassword);
	const handleMouseDownPassword = () => setShowPassword(!showPassword);

	const [failedAttempts, setFailedAttempts] = useState(0);

	const [showForgotPassword, setShowForgotPassword] = useState(false);
	const [forgotEmail, setForgotEmail] = useState('');
	const [forgotLoading, setForgotLoading] = useState(false);
	const [forgotSent, setForgotSent] = useState(false);

	// Multi-company picker: when /auth/login returns requiresCompanySelection
	// (user belongs to >1 company), we stash the short-lived selectionToken
	// and the list of companies, then render a picker instead of the form.
	// Bootstrap selectionToken from the URL (?st=...) on first render so that
	// the standalone tenant-select flow from Settings → User lands directly on
	// the picker. The URL param is consumed once on mount; useEffect cleans
	// the address bar afterwards.
	const initialSelectionToken = useMemo(() => {
		try {
			return new URLSearchParams(window.location.search).get('st') || null;
		} catch {
			return null;
		}
	}, []);
	const [selectionToken, setSelectionToken] = useState(initialSelectionToken);
	const [selectableCompanies, setSelectableCompanies] = useState([]);
	const [companySearch, setCompanySearch] = useState('');
	// Mirror of selectionToken for use inside the fetch effect's .catch —
	// lets us suppress the "Valet har gått ut" snackbar when the user has
	// already clicked "Tillbaka till inloggning" while the fetch was in flight.
	const selectionTokenRef = useRef(selectionToken);
	useEffect(() => {
		selectionTokenRef.current = selectionToken;
	}, [selectionToken]);

	// While the browser is navigating to the selected company's domain we
	// swap the picker for a clean transition view so the user sees the
	// company name + spinner instead of the bare picker or a snackbar flash.
	const [redirectingTo, setRedirectingTo] = useState(null);

	// Watchdog: if window.location.href = redirectUrl never produces a
	// browser navigation (invalid URL, blocked by CSP, host unreachable),
	// we'd otherwise leave the user on the "Loggar in i..." spinner forever.
	// After 10s, revert to the form and surface a recoverable error.
	useEffect(() => {
		if (!redirectingTo) return;
		const watchdog = setTimeout(() => {
			setRedirectingTo(null);
			setLoading(false);
			setData(d => ({...d, isSubmitting: false, errorMessage: null}));
			setUpdateMessage('Det gick inte att skicka dig vidare. Försök igen.');
			setUpdateType('error');
			setSnackbarOpen(true);
		}, 10000);
		return () => clearTimeout(watchdog);
	}, [redirectingTo]);

	// Alphabetical (Swedish locale) and filtered by free-text search. The
	// search box only shows for >10 companies — for shorter lists the
	// alphabetical buttons are quick enough.
	const SEARCH_THRESHOLD = 10;
	const filteredCompanies = useMemo(() => {
		const sorted = [...selectableCompanies].sort((a, b) => (a.companyName || '').localeCompare(b.companyName || '', 'sv'));
		const q = companySearch.trim().toLowerCase();
		if (!q) return sorted;
		return sorted.filter(c => (c.companyName || '').toLowerCase().includes(q));
	}, [selectableCompanies, companySearch]);

	// Group filtered companies by active / trading / inactive. Headers only
	// render when more than one group is represented — a restaurant owner with
	// 12 trading-only venues doesn't need a "Trading only" banner above them.
	const groupedCompanies = useMemo(() => {
		const byGroup = new Map();
		for (const key of TENANT_GROUP_KEYS) byGroup.set(key, []);
		for (const c of filteredCompanies) byGroup.get(getTenantGroupKey(c)).push(c);
		const nonEmpty = TENANT_GROUP_KEYS.map(key => ({key, companies: byGroup.get(key)})).filter(g => g.companies.length > 0);
		return {groups: nonEmpty, showHeaders: nonEmpty.length > 1};
	}, [filteredCompanies]);

	const [branding, setBranding] = useState(FALLBACK_BRANDING);

	useEffect(() => {
		// Branding is fetched per-host from the loadbalancer's /api/branding
		// endpoint, which looks up the company by domain in the database.
		fetch('/api/branding')
			.then(res => (res.ok ? res.json() : null))
			.then(data => data && setBranding(data))
			.catch(err => console.warn('Failed to load branding:', err));
	}, []);

	const [data, setData] = useState({
		email: '',
		password: '',
		isSubmitting: false,
		errorMessage: null,
	});

	useEffect(() => {
		document.title = branding.title;
	}, [branding.title]);

	// Reflect the picker as its own URL so the page feels like a real route
	// (browser title, shareable URL, back button behaves sensibly).
	useEffect(() => {
		if (selectionToken && window.location.pathname !== '/tenant-select') {
			window.history.pushState({}, '', '/tenant-select');
		} else if (!selectionToken && window.location.pathname === '/tenant-select') {
			window.history.replaceState({}, '', '/');
		}
	}, [selectionToken]);

	// When the picker was reached via a selectionToken from the URL (the
	// "switch company from Settings" flow), the company list isn't in state
	// yet — fetch it from /auth/companies-for-selection. Strip the ?st=…
	// param from the address bar once consumed.
	useEffect(() => {
		if (!initialSelectionToken) return;
		// Clean the URL immediately so the token isn't left in history.
		window.history.replaceState({}, '', '/tenant-select');
		axiosClient
			.post('/auth/companies-for-selection', {selectionToken: initialSelectionToken})
			.then(res => {
				setSelectableCompanies(res.data.companies || []);
			})
			.catch(err => {
				// User clicked "Tillbaka till inloggning" before the fetch settled —
				// don't pop a stale error toast over the login form.
				if (!selectionTokenRef.current) return;
				const message = err.response?.data?.errorMessage || 'Valet har gått ut. Logga in igen.';
				setSelectionToken(null);
				setSelectableCompanies([]);
				setUpdateMessage(message);
				setUpdateType('error');
				setSnackbarOpen(true);
			});
	}, [initialSelectionToken]);

	// Browser back/forward navigation should clear the picker, not strand the
	// user on /tenant-select with React state surviving a URL change. Without
	// this, clicking browser-back from the picker visibly does nothing (URL
	// rewinds to /, but the picker keeps rendering off React state).
	useEffect(() => {
		const onPop = () => {
			if (window.location.pathname !== '/tenant-select') {
				setSelectionToken(null);
				setSelectableCompanies([]);
				setCompanySearch('');
			}
		};
		window.addEventListener('popstate', onPop);
		return () => window.removeEventListener('popstate', onPop);
	}, []);

	const emailRef = useRef(null);
	const passwordRef = useRef(null);

	useEffect(() => {
		if (emailRef.current && emailRef.current.value) {
			emailRef.current.focus();
			emailRef.current.blur();
		}
		if (passwordRef.current && passwordRef.current.value) {
			passwordRef.current.focus();
			passwordRef.current.blur();
		}
	}, []);

	const handleInputChange = event => {
		const {name, value} = event.target;
		setData(prevData => ({...prevData, [name]: value}));
	};

	const handleCloseSnackbar = () => {
		setSnackbarOpen(false);
	};

	const handleSubmit = async e => {
		e.preventDefault();
		setLoading(true);
		setData({...data, isSubmitting: true, errorMessage: null});

		try {
			const response = await axiosClient.post('/auth/login', {
				email: data.email,
				password: data.password,
			});

			// Server requires the user to pick which company to enter when they
			// belong to more than one (typically restaurant owners with multiple
			// venues). Don't redirect yet — render the picker.
			if (response.data.requiresCompanySelection) {
				setSelectionToken(response.data.selectionToken);
				setSelectableCompanies(response.data.companies || []);
				setData({...data, isSubmitting: false, errorMessage: null});
				setLoading(false);
				return;
			}

			if (response.data.success) {
				// Keep isSubmitting/loading true so the form doesn't flash back —
				// the redirecting screen takes over until the browser navigates.
				setRedirectingTo({
					_id: null,
					companyName: response.data.companyName || '',
					icon: response.data.icon || '',
				});

				let redirectUrl;
				if (response.data.redirectUrl) {
					redirectUrl = response.data.redirectUrl;
					const intendedPath = sessionStorage.getItem('redirectAfterLogin');
					if (intendedPath && intendedPath !== '/') {
						redirectUrl = `${redirectUrl}${intendedPath}`;
						sessionStorage.removeItem('redirectAfterLogin');
					}
				} else {
					const intendedPath = sessionStorage.getItem('redirectAfterLogin');
					if (intendedPath && intendedPath !== '/') {
						redirectUrl = `${getAppUrl()}${intendedPath}`;
						sessionStorage.removeItem('redirectAfterLogin');
					} else {
						redirectUrl = getAppUrl();
					}
				}

				console.log('Redirecting to:', redirectUrl);
				window.location.href = redirectUrl;
			}
		} catch (error) {
			console.error('Login error:', error);
			const serverErrorMessage = error.response?.data?.errorMessage || 'Inloggningen misslyckades. Vänligen kontrollera dina uppgifter och försök igen.';

			setFailedAttempts(prev => prev + 1);
			setData({
				...data,
				isSubmitting: false,
				errorMessage: serverErrorMessage,
			});
			setLoading(false);
			setUpdateMessage(serverErrorMessage);
			setUpdateType('error');
			setSnackbarOpen(true);
		}
	};

	const handleSelectCompany = async companyId => {
		const targetCompany = selectableCompanies.find(c => c._id === companyId);
		setRedirectingTo(targetCompany || {_id: companyId, companyName: ''});
		setData({...data, isSubmitting: true, errorMessage: null});

		try {
			const response = await axiosClient.post('/auth/select-company', {
				selectionToken,
				companyId,
			});

			if (response.data.success) {
				let redirectUrl;
				if (response.data.redirectUrl) {
					redirectUrl = response.data.redirectUrl;
					const intendedPath = sessionStorage.getItem('redirectAfterLogin');
					if (intendedPath && intendedPath !== '/') {
						redirectUrl = `${redirectUrl}${intendedPath}`;
						sessionStorage.removeItem('redirectAfterLogin');
					}
				} else {
					const intendedPath = sessionStorage.getItem('redirectAfterLogin');
					if (intendedPath && intendedPath !== '/') {
						redirectUrl = `${getAppUrl()}${intendedPath}`;
						sessionStorage.removeItem('redirectAfterLogin');
					} else {
						redirectUrl = getAppUrl();
					}
				}

				// Keep the transition screen visible — don't toggle loading
				// or fire snackbars that would flash before navigation starts.
				window.location.href = redirectUrl;
			}
		} catch (error) {
			const serverErrorMessage = error.response?.data?.errorMessage || 'Valet kunde inte slutföras. Försök igen.';

			// Selection token expired (~2 min TTL) — kick back to login form.
			if (error.response?.status === 401) {
				setSelectionToken(null);
				setSelectableCompanies([]);
				setCompanySearch('');
			}

			// Pull the transition screen down so the user can retry or see the error.
			setRedirectingTo(null);
			setData({...data, isSubmitting: false, errorMessage: serverErrorMessage});
			setLoading(false);
			setUpdateMessage(serverErrorMessage);
			setUpdateType('error');
			setSnackbarOpen(true);
		}
	};

	const handleForgotPasswordSubmit = async e => {
		e.preventDefault();
		setForgotLoading(true);
		try {
			await axiosClient.post('/auth/forgot-password', {email: forgotEmail});
		} catch (_) {
			// Intentionally swallow — never reveal whether account exists
		} finally {
			setForgotLoading(false);
			setForgotSent(true);
		}
	};

	const handleBackToLogin = () => {
		setShowForgotPassword(false);
		setForgotSent(false);
		setForgotEmail('');
	};

	// CSS variables that LoginPage.css picks up via var(--brand-...).
	const brandStyle = {
		'--brand-primary': branding.primaryColor,
		'--brand-primary-hover': branding.primaryColorHover,
		'--brand-button-text': branding.buttonTextColor,
		'--brand-card-bg': branding.cardBackground,
		'--brand-card-text': branding.cardTextColor,
		'--brand-right-gradient': branding.rightPanelGradient,
		...(branding.leftBackgroundImage ? {'--brand-left-bg': `url("${branding.leftBackgroundImage}")`} : {}),
	};

	// MUI's TextField does NOT inherit `color` from an ancestor for its
	// label/input/border — those come from MUI's own theme classes, always
	// dark by default. Without this, a customized dark cardBackground makes
	// the email/password fields invisible regardless of cardTextColor, since
	// cardTextColor only affects plain text (e.g. the "Welcome back" heading)
	// that genuinely inherits CSS color.
	const cardFieldSx = {
		'& .MuiInputLabel-root': {color: branding.cardTextColor},
		'& .MuiInputLabel-root.Mui-focused': {color: branding.primaryColor},
		'& .MuiOutlinedInput-root': {
			color: branding.cardTextColor,
			'& fieldset': {borderColor: branding.cardTextColor},
			'&:hover fieldset': {borderColor: branding.primaryColor},
			'&.Mui-focused fieldset': {borderColor: branding.primaryColor},
		},
		'& .MuiIconButton-root': {color: branding.cardTextColor},
	};

	const forgotPasswordForm = (
		<Grid container spacing={1}>
			{forgotSent ? (
				<Grid item xs={12}>
					<Alert severity="success" sx={{mb: 2}}>
						Om det finns ett konto som matchar <strong>{forgotEmail}</strong> skickas en inloggningslänk ut.
					</Alert>
					<Button fullWidth variant="text" onClick={handleBackToLogin} sx={{textTransform: 'none', color: branding.primaryColor}}>
						Tillbaka till inloggning
					</Button>
				</Grid>
			) : (
				<>
					<Grid item xs={12}>
						<Typography variant="body2" color="text.secondary" sx={{mb: 1}}>
							Ange din e-postadress så skickar vi en inloggningslänk.
						</Typography>
						<FormControl fullWidth margin="dense">
							<TextField
								fullWidth
								margin="dense"
								label="E-postadress"
								variant="outlined"
								autoComplete="username"
								type="text"
								value={forgotEmail}
								onChange={e => setForgotEmail(e.target.value)}
								autoFocus
								InputLabelProps={{shrink: true}}
								sx={cardFieldSx}
							/>
						</FormControl>
					</Grid>
					<Grid item xs={12}>
						<FormControl fullWidth margin="normal">
							<Button
								disabled={forgotLoading || !forgotEmail}
								size="large"
								onClick={handleForgotPasswordSubmit}
								style={{
									background: branding.primaryColor,
									padding: '15px',
									textTransform: 'none',
									color: branding.buttonTextColor,
									borderRadius: '8px',
								}}
								variant="contained"
							>
								{forgotLoading ? <CircularProgress size={22} sx={{color: branding.buttonTextColor}} /> : 'Skicka länk'}
							</Button>
						</FormControl>
					</Grid>
					<Grid item xs={12} sx={{textAlign: 'center'}}>
						<Button
							variant="text"
							onClick={handleBackToLogin}
							sx={{
								textTransform: 'none',
								color: branding.primaryColor,
								fontSize: '0.8rem',
							}}
						>
							Tillbaka till inloggning
						</Button>
					</Grid>
				</>
			)}
		</Grid>
	);

	const loginForm = (
		<Grid container spacing={1}>
			<Grid item xs={12}>
				<FormControl fullWidth margin="dense">
					<TextField
						fullWidth
						margin="dense"
						id="outlined-multiline-flexible"
						label={t('login.username')}
						variant="outlined"
						name="email"
						type="text"
						autoComplete="username"
						value={data.email}
						onChange={handleInputChange}
						autoFocus
						InputLabelProps={{shrink: true}}
						onKeyDown={e => {
							if (e.key === 'Enter') {
								e.preventDefault();
								passwordRef.current?.focus();
							}
						}}
						sx={cardFieldSx}
					/>
				</FormControl>
			</Grid>
			<Grid item xs={12}>
				<FormControl fullWidth margin="dense">
					<TextField
						fullWidth
						margin="dense"
						id="outlined-multiline-flexible"
						label={t('login.password')}
						variant="outlined"
						name="password"
						autoComplete="current-password"
						type={showPassword ? 'text' : 'password'}
						inputRef={passwordRef}
						InputProps={{
							endAdornment: (
								<InputAdornment position="end">
									<IconButton aria-label="toggle password visibility" onClick={handleClickShowPassword} onMouseDown={handleMouseDownPassword}>
										{showPassword ? <Visibility /> : <VisibilityOff />}
									</IconButton>
								</InputAdornment>
							),
						}}
						value={data.password}
						onChange={handleInputChange}
						sx={cardFieldSx}
					/>
				</FormControl>
			</Grid>
			<Grid item xs={12}>
				<FormControl fullWidth margin="normal">
					<Button
						className="login-button"
						disabled={data.isSubmitting}
						size="large"
						style={{
							background: branding.primaryColor,
							padding: '15px',
							textTransform: 'none',
							color: branding.buttonTextColor,
							borderRadius: '8px',
							transition: 'background-color 0.3s',
						}}
						type="submit"
						variant="contained"
						onMouseOver={e => {
							e.target.style.backgroundColor = branding.primaryColorHover;
						}}
						onMouseOut={e => {
							e.target.style.backgroundColor = branding.primaryColor;
						}}
					>
						{t('login.login')}
					</Button>
				</FormControl>
			</Grid>
			{failedAttempts >= 1 && (
				<Grid item xs={12} sx={{textAlign: 'center'}}>
					<Button
						variant="text"
						onClick={() => {
							setForgotEmail(data.email);
							setShowForgotPassword(true);
						}}
						sx={{
							textTransform: 'none',
							color: branding.primaryColor,
							fontSize: '0.8rem',
							textDecoration: 'underline',
						}}
					>
						Glömt lösenord?
					</Button>
				</Grid>
			)}
		</Grid>
	);

	const cardBody = withTitle => (
		<>
			{withTitle && (
				<Box sx={{display: 'flex', gap: '1rem', alignItems: 'center', mb: 1}}>
					<Typography variant="h5" component="h1" gutterBottom>
						{showForgotPassword ? 'Glömt lösenord?' : t('login.welcomeback')}
					</Typography>
				</Box>
			)}
			{loading ? (
				<Grid container>
					<Grid item xs={12} style={{padding: '3rem', textAlign: 'center'}}>
						<CircularProgress style={{margin: '2rem auto'}} />
					</Grid>
				</Grid>
			) : showForgotPassword ? (
				forgotPasswordForm
			) : (
				<form onSubmit={handleSubmit}>{loginForm}</form>
			)}
		</>
	);

	// In-flight transition: between "company clicked" and the browser actually
	// navigating to the target subdomain, show a small Partihandel mark, the
	// target company's own logo prominently in the middle, and a status line.
	// Matches the white picker aesthetic for a smooth visual handoff.
	if (redirectingTo) {
		return (
			<Box
				sx={{
					minHeight: '100vh',
					backgroundColor: '#fff',
					color: '#000',
					display: 'flex',
					flexDirection: 'column',
					alignItems: 'center',
					justifyContent: 'center',
					px: 2,
					gap: 2.5,
					position: 'relative',
				}}
			>
				{/* Small Partihandel mark, pinned near the top. Never shown on a customer-controlled domain. */}
				{!isCustomerControlledDomain && (
					<img src="https://storage.googleapis.com/pifa.se/partihandel.se_row.png" alt="Partihandel" style={{height: 28, opacity: 0.7, position: 'absolute', top: 24}} />
				)}

				{redirectingTo.icon ? (
					<img src={redirectingTo.icon} alt={redirectingTo.companyName || ''} style={{maxHeight: 140, maxWidth: 320, objectFit: 'contain'}} />
				) : (
					<Box
						sx={{
							width: 120,
							height: 120,
							borderRadius: '50%',
							backgroundColor: '#f2f2f2',
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
							fontSize: '2.6rem',
							fontWeight: 600,
							color: '#444',
						}}
					>
						{(redirectingTo.companyName || '?').trim().charAt(0).toUpperCase()}
					</Box>
				)}

				<Typography variant="h6" component="div" sx={{fontWeight: 500, color: '#1a1a1a', textAlign: 'center', mt: 1}}>
					Loggar in i <strong>{redirectingTo.companyName || '…'}</strong>
				</Typography>
				<CircularProgress size={24} sx={{color: '#1a1a1a'}} />
			</Box>
		);
	}

	// Dedicated /tenant-select page — white background, black logo,
	// Fortnox-style company cards with a status indicator on the left
	// of each row (green / red dot or trending icon).
	if (selectionToken && !showForgotPassword) {
		return (
			<Box
				sx={{
					minHeight: '100vh',
					backgroundColor: '#fff',
					color: '#000',
					display: 'flex',
					flexDirection: 'column',
				}}
			>
				{/* Language selector floats top-right; logo is centered below. */}
				<Box sx={{position: 'absolute', top: 16, right: 16, background: '#ffffff', borderRadius: '5px', zIndex: 10}}>
					<LanguageSelector color={'black'} />
				</Box>

				<Box
					sx={{
						flex: 1,
						display: 'flex',
						flexDirection: 'column',
						alignItems: 'center',
						justifyContent: 'flex-start',
						px: {xs: 2, sm: 4},
						pt: {xs: 4, sm: 6},
						pb: {xs: 2, sm: 4},
					}}
				>
					<Box sx={{width: '100%', maxWidth: 760, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3}}>
						{!isCustomerControlledDomain && (
							<img src="https://storage.googleapis.com/pifa.se/partihandel.se_row.png" alt="Partihandel" style={{height: 40}} />
						)}

						<Box sx={{textAlign: 'center'}}>
							<Typography variant="h5" component="h1" sx={{fontWeight: 600, mb: 0.75, color: '#1a1a1a'}}>
								Välj företag
							</Typography>
							<Typography variant="body2" sx={{color: 'rgba(0,0,0,0.6)'}}>
								Du har åtkomst till flera företag.
								<br />
								Välj vilket du vill logga in i.
							</Typography>
						</Box>

						{selectableCompanies.length > SEARCH_THRESHOLD && (
							<TextField
								fullWidth
								size="small"
								name="company-search"
								placeholder="Sök företag…"
								value={companySearch}
								onChange={e => setCompanySearch(e.target.value)}
								autoFocus
								autoComplete="off"
								inputProps={{
									autoCapitalize: 'none',
									autoCorrect: 'off',
									spellCheck: false,
									autoComplete: 'off',
									'data-form-type': 'other',
									'data-lpignore': 'true',
								}}
							/>
						)}

						{/* Grouped list of companies */}
						<Box sx={{width: '100%', display: 'flex', flexDirection: 'column', gap: 1, maxHeight: '60vh', overflowY: 'auto', pr: 0.5}}>
							{filteredCompanies.length === 0 ? (
								<Typography variant="body2" sx={{textAlign: 'center', py: 4, color: 'rgba(0,0,0,0.5)'}}>
									{selectableCompanies.length === 0 ? 'Det finns inga företag kopplade till ditt konto. Kontakta supporten.' : 'Inga företag matchar sökningen.'}
								</Typography>
							) : (
								groupedCompanies.groups.map(group => {
									const groupMeta = TENANT_GROUP_META[group.key];
									return (
										<React.Fragment key={group.key}>
											{groupedCompanies.showHeaders && (
												<Box sx={{display: 'flex', alignItems: 'center', gap: 1, mt: 1.5, mb: 0.25, px: 0.5}}>
													<Box sx={{width: 8, height: 8, borderRadius: '50%', bgcolor: groupMeta.color, flexShrink: 0}} />
													<Typography sx={{color: 'rgba(0,0,0,0.55)', fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em'}}>{groupMeta.label}</Typography>
												</Box>
											)}
											{group.companies.map(company => (
												<Box
													key={company._id}
													onClick={() => handleSelectCompany(company._id)}
													role="button"
													tabIndex={0}
													onKeyDown={e => {
														if (e.key === 'Enter' || e.key === ' ') {
															e.preventDefault();
															handleSelectCompany(company._id);
														}
													}}
													sx={{
														backgroundColor: '#fff',
														color: '#000',
														border: '1px solid #e6e6e6',
														borderRadius: '10px',
														px: 2.5,
														py: 1.75,
														display: 'flex',
														alignItems: 'center',
														gap: 1.5,
														cursor: 'pointer',
														transition: 'background-color 0.15s, border-color 0.15s, transform 0.15s',
														'&:hover': {backgroundColor: '#fafafa', borderColor: '#cfcfcf'},
														'&:active': {transform: 'scale(0.997)'},
														'&:focus-visible': {outline: '2px solid #1976d2', outlineOffset: 2},
													}}
												>
													<TenantRowIndicator groupKey={group.key} />
													<Box sx={{flex: 1, minWidth: 0}}>
														<Typography variant="body1" sx={{fontWeight: 600, color: '#1a1a1a'}}>
															{company.companyName}
														</Typography>
													</Box>
													<ChevronRight sx={{color: '#bdbdbd'}} />
												</Box>
											))}
										</React.Fragment>
									);
								})
							)}
						</Box>

						<Box sx={{textAlign: 'center', pt: 1}}>
							<Button
								variant="text"
								onClick={() => {
									setSelectionToken(null);
									setSelectableCompanies([]);
									setCompanySearch('');
								}}
								sx={{
									textTransform: 'none',
									color: 'rgba(0,0,0,0.6)',
									fontSize: '0.85rem',
									'&:hover': {backgroundColor: 'rgba(0,0,0,0.04)', color: '#000'},
								}}
							>
								Tillbaka till inloggning
							</Button>
						</Box>
					</Box>
				</Box>

				<Snackbar open={snackbarOpen} autoHideDuration={6000} onClose={handleCloseSnackbar} anchorOrigin={{vertical: 'bottom', horizontal: 'center'}}>
					<Alert onClose={handleCloseSnackbar} severity={updateType} sx={{width: '100%'}}>
						{updateMessage}
					</Alert>
				</Snackbar>
			</Box>
		);
	}

	return (
		<div
			className={`login-root${branding.leftBackgroundImage ? ' has-bg-image' : ''}${branding.fullScreenBackgroundImage ? ' full-screen-bg' : ''}`}
			style={brandStyle}
		>
			<Box
				sx={{
					position: 'absolute',
					top: 16,
					right: 16,
					background: '#ffffff',
					borderRadius: '5px',
					zIndex: 10,
				}}
			>
				<LanguageSelector color={'black'} />
			</Box>
			<div className="login-left">
				{!isMobileScreen && !branding.leftBackgroundImage && (
					<Box className="logo-container">
						<a href={getStartUrl()}>
							{branding.logoUrl && <img src={branding.logoUrl} alt="Logo" className="logoimg" style={branding.logoStyle} />}
						</a>
					</Box>
				)}

				<Card className="login-card-mobile" sx={{backgroundColor: branding.cardBackground, color: branding.cardTextColor}}>
					<CardContent>
						{isMobileScreen || (branding.fullScreenBackgroundImage && branding.leftBackgroundImage) ? (
							<Box
								sx={{
									display: 'flex',
									gap: '1rem',
									alignItems: 'center',
									alignContent: 'center',
									justifyContent: 'center',
								}}
							>
								<a href={getStartUrl()}>
									{branding.logoUrl && <img src={branding.logoUrl} alt="Logo" className="logoimg" style={branding.logoStyle} />}
								</a>
							</Box>
						) : (
							<Typography variant="h4" component="h1" gutterBottom>
								{showForgotPassword ? 'Glömt lösenord?' : t('login.welcomeback')}
							</Typography>
						)}
						{cardBody(false)}
					</CardContent>
				</Card>

				{(isMobileScreen || branding.fullScreenBackgroundImage) && (
					<Box
						sx={{
							display: 'flex',
							gap: '1rem',
							justifyContent: 'center',
							mt: 2,
						}}
					>
						<SocialIcons size="2.5rem" />
					</Box>
				)}

				<div className="snackbar-container-mobile" style={{marginTop: '2rem'}}>
					<Snackbar open={snackbarOpen} autoHideDuration={6000} onClose={handleCloseSnackbar}>
						<Alert onClose={handleCloseSnackbar} severity={updateType} sx={{width: '100%'}}>
							{updateMessage}
						</Alert>
					</Snackbar>
				</div>
			</div>

			<div className="login-right">
				<Card className="login-card" sx={{backgroundColor: branding.cardBackground, color: branding.cardTextColor}}>
					<CardContent>
						{branding.leftBackgroundImage ? (
							<Box
								sx={{
									display: 'flex',
									justifyContent: 'center',
									alignItems: 'center',
									mb: 2,
								}}
							>
								<a href={getStartUrl()}>
									{branding.logoUrl && <img src={branding.logoUrl} alt="Logo" className="logoimg" style={branding.logoStyle} />}
								</a>
							</Box>
						) : (
							<Box
								sx={{
									display: 'flex',
									gap: '1rem',
									alignItems: 'center',
									alignContent: 'center',
								}}
							>
								<Typography variant="h5" component="h1" gutterBottom>
									{showForgotPassword ? 'Glömt lösenord?' : t('login.welcomeback')}
								</Typography>
							</Box>
						)}
						{cardBody(false)}
					</CardContent>
				</Card>

				<Box className="social-media">
					<SocialIcons size="22px" />
				</Box>

				<div className="snackbar-container-desktop">
					<Snackbar open={snackbarOpen} autoHideDuration={6000} onClose={handleCloseSnackbar}>
						<Alert onClose={handleCloseSnackbar} severity={updateType} sx={{width: '100%'}}>
							{updateMessage}
						</Alert>
					</Snackbar>
				</div>
			</div>
		</div>
	);
}

export default LoginApp;
